import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Archive } from "lucide-react";
import { MultiSelectField, type MultiSelectOption } from "@/shared/ui/core";
import { ACTIVATION_OPTIONS } from "../productForm";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Las cuatro opciones de activación, en UN selector múltiple.
 *
 * Las cuatro EXISTÍAN ya, repartidas por tres pasos distintos y con
 * otros nombres: `status` y `isAvailable` en clasificación,
 * `qualifiesForRank` en puntos, `shipping.requiresShipping` en envío. El
 * reparto era el problema: para saber si un producto se ve, se compra,
 * califica y se manda había que recorrer medio formulario.
 *
 * Aquí son cuatro caras de una sola pregunta —cómo se comporta este
 * producto— y por eso son un solo control, como pidió Anahí. El mapeo a
 * los campos del contrato vive en `ACTIVATION_OPTIONS`, junto a las dos
 * funciones que lo aplican en cada sentido.
 *
 * ── Es un DESPLEGABLE, no cuatro casillas a la vista ────────────────
 * "Un select múltiple, o sea un input desplegable para elegir varias
 * opciones" (Anahí, 26-ago). Cerrado se lee qué hay puesto; abierto se
 * cambia. Lo que NO cambió es por qué no puede ser un `<select multiple>`
 * nativo: la línea de ayuda de cada opción es media explicación del
 * control, y un `<option>` solo admite texto. Ver `MultiSelect`.
 *
 * ── Las ayudas son literales ────────────────────────────────────────
 * Los cuatro textos de abajo son los que Anahí escribió, palabra por
 * palabra. No son adorno: "Activo en catálogo" y "Disponible para
 * comprar" se leen como sinónimos hasta que se lee la línea de debajo, y
 * apagar la equivocada es la diferencia entre "no se puede comprar" y
 * "no existe".
 *
 * ── Y el estado archivado no cabe en una casilla ────────────────────
 * `status` tiene tres valores. Cuando el producto está archivado, la
 * opción "Activo en catálogo" se enseña apagada y con un aviso al pie:
 * quien mira tiene que ver POR QUÉ no está en la tienda, y "archivado"
 * no es lo mismo que "sin publicar". Volver a publicarlo se hace desde
 * el listado, con su diálogo, porque desarchivar es una decisión con
 * consecuencias y no una casilla.
 * ═══════════════════════════════════════════════════════════════════
 */
export function ProductActivationField() {
  const { t } = useTranslation();
  const status = useWatch({ name: "status" }) as string | undefined;
  const archivado = status === "archived";

  const options: MultiSelectOption[] = [
    {
      value: ACTIVATION_OPTIONS.catalogActive,
      label: t("Activo en catálogo"),
      hint: t("Si está desactivado, el producto no será visible en la tienda"),
      // Un archivado no se publica desde aquí: se desarchiva desde el
      // listado. Dejar la opción pulsable sería ofrecer una acción que
      // tiene consecuencias sin la confirmación que ya existe para ella.
      disabled: archivado,
    },
    {
      value: ACTIVATION_OPTIONS.purchasable,
      label: t("Disponible para comprar"),
      hint: t(
        "Si está desactivado, el producto aparece en el catálogo pero el botón Agregar al carrito queda inhabilitado",
      ),
    },
    {
      value: ACTIVATION_OPTIONS.qualifying,
      label: t("Paquete calificador"),
      hint: t("La compra de este producto activa al socio para recibir comisiones"),
    },
    {
      value: ACTIVATION_OPTIONS.requiresShipping,
      label: t("Requiere envío"),
      hint: t("Producto físico que necesita logística de envío"),
    },
  ];

  return (
    <div className="grid gap-1.5">
      <MultiSelectField
        name="activation"
        label={t("Opciones de activación")}
        options={options}
        // Cerrado ocupa lo que un campo cualquiera y resume lo elegido
        // por su nombre ("Activo en catálogo, +2"). Las cuatro opciones
        // con su línea de ayuda a la vista eran ocho renglones fijos en
        // un paso que pelea por caber en una vista. Ver `MultiSelect`.
        placeholder={t("Elige cómo se comporta…")}
      />

      {archivado && (
        <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-ds-ink-3">
          <Archive aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
          {t(
            "Este producto está archivado: no aparece en la tienda y guardar aquí no lo devuelve. Para volver a publicarlo, usa el listado de productos.",
          )}
        </p>
      )}
    </div>
  );
}
